/**
 * QA AI Agent Frontend Application
 *
 * Main application component with authentication and routing
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Alert, Box, Button } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Pages
import ChatPage from './pages/ChatPage';
import LoginPage from './pages/LoginPage';
import RecorderPage from './pages/RecorderPage';
import LoadingPage from './pages/LoadingPage';
import UsagePage from './pages/UsagePage';
import AdminMetricsPage from './pages/AdminMetricsPage';

// Hooks
import useAuth from './hooks/useAuth';

// Components
import BuildUpdateNotice from './components/BuildUpdateNotice';
import { AppSwitcher } from './components/AppSwitcher';

// Create Material UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#059669', // Emerald green for QA Agent branding
      light: '#34d399',
      dark: '#047857',
    },
    secondary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 1,
    },
  },
});

/**
 * Protected Route Component
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, error, login, checkAuth } = useAuth();

  if (isLoading) {
    return <LoadingPage />;
  }

  // The session could not be checked (server unreachable, 5xx, or timed
  // out). That is not "signed out": say so and offer a retry rather than
  // bouncing to login or sitting on the spinner.
  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 2 }}>
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => { void checkAuth(); }}>Retry</Button>}
        >
          This page couldn&apos;t load: {error}
        </Alert>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login
    login();
    return <LoadingPage message="Redirecting to login..." />;
  }

  return <>{children}</>;
};

/**
 * Main App Component
 */
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BuildUpdateNotice />
        {/* Cross-app product switcher — floats top-right on every page */}
        <AppSwitcher />
        <BrowserRouter>
          <Routes>
            {/* Login callback route */}
            <Route path="/login" element={<LoginPage />} />

            {/* Main chat interface (protected) */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />

            {/* Full-screen record-and-generate (opened in its own tab) */}
            <Route
              path="/record"
              element={
                <ProtectedRoute>
                  <RecorderPage />
                </ProtectedRoute>
              }
            />

            {/* Token usage + org AI wallet */}
            <Route
              path="/usage"
              element={
                <ProtectedRoute>
                  <UsagePage />
                </ProtectedRoute>
              }
            />

            {/* Org-admin agent metrics */}
            <Route
              path="/admin/metrics"
              element={
                <ProtectedRoute>
                  <AdminMetricsPage />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
